import React, { useState, useContext, useEffect } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import { DataContext } from "../../../context/dataprovider.js";
import apiClient from "../../../api/Api.js";
import PDFFilter from "../../Filters/pdf/PDFFilterCard.js";
import PDFTable from "./PDFTable.js";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

const TemplateFilterCard = () => {
  const TRANSACTIONS_API = process.env.REACT_APP_TRANSACTIONS_API;
  const { userData = {}, statusDef } = useContext(DataContext);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState([]);

  // Helper to get filters with defaults applied
  const getFiltersWithDefaults = () => {
    const queryFilters = { ...filters };

    if (!queryFilters.projectId) {
      queryFilters.projectId = userData.projectId?.[0];
    }
    if (queryFilters.status === null || queryFilters.status === undefined) {
      queryFilters.status = [
        statusDef.phase4,
        statusDef.phase5,
        statusDef.final,
      ];
    }
    if (queryFilters.status.length === 0) {
      queryFilters.status = [
        statusDef.phase4,
        statusDef.phase5,
        statusDef.final,
      ];
    }
    return queryFilters;
  };

  // Separated API function as per shared example
  const getTableData = async (page = 1, inputFilters = {}) => {
    const queryFilters = { ...inputFilters };

    if (!queryFilters.projectId) {
      queryFilters.projectId = userData.projectId?.[0];
    }
    if (
      queryFilters.status === null ||
      queryFilters.status === undefined ||
      queryFilters.status.length === 0
    ) {
      queryFilters.status = [
        statusDef.phase4,
        statusDef.phase5,
        statusDef.final,
      ];
    }

    try {
      const response = await apiClient.get(
        `${TRANSACTIONS_API}/gettrans/${userData.email}`,
        {
          params: {
            page: page,
            limit: 10,
            ...queryFilters,
          },
        }
      );

      // console.log("Response from API:", response.data);
      setTotalPages(response.data.totalPages);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const { data, error, isLoading } = useQuery({
    queryKey: ["transactions", userData.email, filters, currentPage],
    queryFn: () => getTableData(currentPage, filters),
    enabled: !!userData.email,
    keepPreviousData: true,
  });

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    setSelectedTransactions([]);
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      let payload = {};

      if (selectedTransactions.length > 0) {
        payload = {
          transId: selectedTransactions,
          projectId: filters.projectId || userData.projectId[0],
          status: [statusDef.phase4, statusDef.phase5, statusDef.final],
        };
      } else {
        payload = getFiltersWithDefaults();
      }

      const response = await apiClient.post(
        `${process.env.REACT_APP_PDF_API}/download`,
        payload
      );

      const { pdf, failedTransactionIDs } = response.data;
      if (!pdf) {
        if (failedTransactionIDs && failedTransactionIDs.length > 0) {
          alert(
            "The following transaction IDs have missing required fields: \n" +
              failedTransactionIDs.join(" , ")
          );
        } else {
          alert("Some fields are missing.");
        }
        return;
      }
      // Convert base64 to blob and download
      const byteCharacters = atob(pdf);
      const byteNumbers = Array.from(byteCharacters, (c) => c.charCodeAt(0));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "transaction_report.pdf";
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      alert("Something went wrong during PDF download.");
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  useEffect(() => {
    if (userData.email) {
      handleApplyFilters({});
    }
  }, [userData.email]);

  return (
    <Box sx={{ minHeight: "100vh", py: 4 }}>
      <Paper
        elevation={6}
        sx={{
          width: "100%",
          margin: "auto",
          p: 4,
          borderRadius: 3,
          backgroundColor: "#fff",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            color: "#1976D2",
            fontWeight: "bold",
            mb: 3,
          }}
        >
          Download Payment Vouchers
        </Typography>

        <PDFFilter userData={userData} onApplyFilters={handleApplyFilters} />

        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={loading}
          fullWidth
          sx={{
            fontWeight: "bold",
            borderRadius: 2,
            py: 1.5,
            textTransform: "none",
            fontSize: "16px",
            mt: 2,
          }}
        >
          {loading ? "Generating PDF..." : "Download PDF"}
        </Button>

        {/* Pagination */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button
            variant="contained"
            onClick={handlePrevPage}
            disabled={currentPage === 1 || isLoading}
            sx={{ mr: 2 }}
          >
            Previous
          </Button>
          <Typography variant="body1" sx={{ alignSelf: "center" }}>
            Page {currentPage} of {totalPages}
          </Typography>
          <Button
            variant="contained"
            onClick={handleNextPage}
            disabled={
              currentPage === totalPages || isLoading || totalPages === 0
            }
            sx={{ ml: 2 }}
          >
            Next
          </Button>
        </Box>

        {/* Table */}
        <PDFTable
          tableData={data?.transactions || []}
          selectedTransactions={selectedTransactions}
          setSelectedTransactions={setSelectedTransactions}
          currentPage={currentPage}
        />
      </Paper>
    </Box>
  );
};

export default TemplateFilterCard;
